import { useMutation, useQuery } from '@apollo/client';
import { BuildingStorefrontIcon, EyeIcon, TrashIcon } from '@heroicons/react/20/solid';
import dayjs from 'dayjs';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Button from '../../../components/buttons/Button';
import Modal from '../../../components/modal/Modal';
import Table from '../../../components/table/Table';
import { useToast } from '../../../components/toast/ToastProvider';
import { Link, Subtitle, Title } from '../../../components/typography';
import { DELETE_DEPARTMENT } from '../../../graphql/departments/mutations';
import { GET_DEPARTMENT, GET_DEPARTMENTS } from '../../../graphql/departments/queries';
import Form from './sharedComponents/Form';

function Departments() {
  const { _id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { data } = useQuery(GET_DEPARTMENTS);
  const [open, setOpen] = useState(false);
  const [deleteDepartment] = useMutation(DELETE_DEPARTMENT);
  const { data: department } = useQuery(GET_DEPARTMENT, {
    variables: { _id }
  });

  const handleClick = async (id) => {
    const res = await deleteDepartment({
      variables: { _id: id },
      refetchQueries: [{ query: GET_DEPARTMENTS }, 'GetDepartments']
    });

    toast({
      title: res.data?.deleteDepartment?.error ? 'Department not deleted' : 'Department deleted',
      description:
        res.data?.deleteDepartment?.message ||
        `The department name ${res.data?.deleteDepartment?.name} has been deleted successfully.`,
      type: res.data?.deleteDepartment?.error ? 'error' : 'success'
    });
  };

  const columns = useMemo(
    () => [
      {
        accessor: 'name',
        Header: 'Name'
      },
      {
        accessor: 'createdBy',
        Header: 'Creator',
        Cell: ({ value }) => {
          return <Link href={`mailto:${value}`} text={value} />;
        }
      },
      {
        accessor: 'createdAt',
        Header: 'Date',
        Cell: ({ value }) => {
          return <>{dayjs(value).format('LLL')}</>;
        }
      },
      {
        accessor: 'actions',
        Header: '',
        Cell: ({ row }) => {
          return (
            <div className="flex items-center justify-center space-x-2">
              <Button
                type="button"
                color="text-red-500"
                Icon={TrashIcon}
                rounded
                outline
                onClick={(e) => {
                  e.stopPropagation();
                  toast({
                    type: 'warning',
                    title: 'Delete department',
                    autoClose: false,
                    description: 'Are you sure you want to delete this department?',
                    onAccept: () => handleClick(row.original._id)
                  });
                }}
              />
              <Button
                type="button"
                color="text-sky-500"
                Icon={EyeIcon}
                rounded
                outline
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/employees/departments/${row.original._id}`);
                }}
              />
            </div>
          );
        },
        disableSortBy: true
      }
    ],
    []
  );

  useEffect(() => {
    if (_id) setOpen(true);
  }, [_id]);

  return (
    <>
      <Modal
        open={open}
        closeModal={() => setOpen(false)}
        title="Department Form"
        description="Use the following request form to create or edit a department."
        onExitComplete={() => {
          if (_id) {
            navigate('/employees/departments/');
          }
        }}
      >
        <Form onSuccess={() => setOpen(false)} data={department?.department} />
      </Modal>
      <Title text="Departments" />
      <Subtitle text="This section is intended for managing employees departments . Please use this section to create, read, update or delete a department." />
      <Table
        columns={columns}
        data={data?.departments || []}
        onRowClick={(row) => navigate(`/employees/departments/${row?._id}`)}
        toolbar={
          <div className="flex items-center">
            <Button
              label="New Department"
              Icon={BuildingStorefrontIcon}
              backgroundColor="bg-green-500"
              onClick={() => setOpen(true)}
            />
          </div>
        }
      />
    </>
  );
}

export default Departments;
